import TalkJS from 'talkjs';
import DialogService from './DialogService';
import { ChatUser } from './types/chat';

export * from './types/chat';

enum MessageEventType {
    PROFILE_SHARING_REQUEST = 'PROFILE_SHARING_REQUEST',
    PROFILE_SHARING_RESPONSE = 'PROFILE_SHARING_RESPONSE',
}

export enum ProfileSharingStatus {
    None,
    Active,
    Pending,
    Rejected,
}

interface MessageEvent {
    type: MessageEventType;
    payload: any;
}

class TalkJSService {

    private static activeInbox: TalkJS.Inbox | null;
    private static activeInboxSubscription: TalkJS.Subscription;

    static handleEvents(
        session: TalkJS.Session,
        chatUser: ChatUser,
        message: TalkJS.Message,
        events: MessageEvent[],
        env: {[key: string]: string},
    ) {
        for (const event of events) {
            TalkJSService.handleEvent(session, chatUser, message, event, env);
        }
    }

    static handleEvent(
        session: TalkJS.Session,
        chatUser: ChatUser,
        message: TalkJS.Message,
        messageEvent: MessageEvent,
        env: {[key: string]: string},
    ) {
        const conversation: TalkJS.ConversationBuilder = session.getOrCreateConversation(
            message.conversation.id
        );

        if (messageEvent.type === MessageEventType.PROFILE_SHARING_REQUEST) {
            const senderName = message.sender!!.name;
            const profileSharingConsent = messageEvent.payload;
            const confirmAction = () => {
                TalkJSService.confirmProfileSharing(conversation, chatUser.displayName, profileSharingConsent);
            };
            const rejectAction = () => {
                TalkJSService.rejectProfileSharing(conversation, chatUser.displayName, profileSharingConsent);
            };
            if (TalkJSService.activeInbox) {
                DialogService.openProfileSharingConfirmationPanel(TalkJSService.activeInbox, conversation, senderName, profileSharingConsent, confirmAction, rejectAction, env);
            } else {
                DialogService.openProfileSharingConfirmationDialog(senderName, profileSharingConsent, confirmAction, rejectAction);
            }
        }
    }

    static confirmProfileSharing(conversation: TalkJS.ConversationBuilder, chatUser: string, profileSharingConsent: object): void {
        TalkJSService.sendPayloadMessage(
          conversation,
          `${chatUser} accepted the request of sharing the profile!`,
          {
              events: JSON.stringify([
                  {
                      type: MessageEventType.PROFILE_SHARING_RESPONSE,
                      payload: profileSharingConsent,
                  },
              ]),
          }
        );
    }

    static rejectProfileSharing(conversation: TalkJS.ConversationBuilder, chatUser: string, profileSharingConsent: object): void {
        TalkJSService.sendPayloadMessage(
          conversation,
          `${chatUser} rejected the sharing of the profile!`,
          {
              events: JSON.stringify([
                  {
                      type: MessageEventType.PROFILE_SHARING_RESPONSE,
                      payload: profileSharingConsent,
                  },
              ]),
          }
        );
    }

    static sendPayloadMessage(
        conversation: TalkJS.ConversationBuilder,
        message: string,
        custom: TalkJS.CustomData
    ) {
        conversation.sendMessage(`\`\`\`${message}\`\`\``, {
            custom: custom,
        });
    }

    static getSession(user: ChatUser) {
        const talkJSUser = new TalkJS.User(user.id);

        return new TalkJS.Session({
            appId: process.env.VUE_APP_TALKJS_APPID,
            me: talkJSUser,
            signature: user.signature,
        });
    }

    static setActiveInbox(activeInbox: TalkJS.Inbox | null, session?: TalkJS.Session, env?: {[key: string]: string},): void {
        if (activeInbox && session) {
            TalkJSService.activeInbox = activeInbox;
            TalkJSService.activeInboxSubscription = activeInbox?.onConversationSelected((event: TalkJS.ConversationSelectedEvent) => {
                const conversation = event?.conversation;
                const metadata = conversation?.custom;
                if (conversation && metadata?.profileRequestPanelOpened) {
                    setTimeout(() => {
                        const user = metadata?.profileRequestPanelUser as string;
                        const consent = metadata.profileRequestPanelConsent ? JSON.parse(metadata.profileRequestPanelConsent) : null;
                        const conversationBuilder = session.getOrCreateConversation(conversation.id);
                        const confirmAction = () => {
                            TalkJSService.confirmProfileSharing(conversationBuilder, user, consent);
                        };
                        const rejectAction = () => {
                            TalkJSService.rejectProfileSharing(conversationBuilder, user, consent);
                        };
                        DialogService.openProfileSharingConfirmationPanel(activeInbox, conversationBuilder, user, consent, confirmAction, rejectAction, env);
                    });
                } else {
                    DialogService.destroyProfilerSharingConfirmationPanel();
                }
            });
        } else {
            TalkJSService.activeInbox = null;
            TalkJSService.activeInboxSubscription && TalkJSService.activeInboxSubscription.unsubscribe();
        }
    }

}

export default TalkJSService;
